/**
 * AgentSelfView.jsx
 * Persoenliche Ansicht fuer Mitarbeiter.
 *
 * Filter wird vom Backend angewendet via Query-Param ?selfView=true:
 *   - keine "kein_support" / "nicht_erreicht" / etc. Tickets
 *   - keine merged-Duplikate
 *   - keine pure internen Forwards
 *
 * Damit sind KPIs, Aktueller Stand, Tickets-Liste und Qualitaet konsistent
 * auf den gleichen reduzierten Pool gerechnet.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AgentDetailKPIs      from './AgentDetailKPIs';
import AgentDetailTickets   from './AgentDetailTickets';
import AgentDetailLive      from './AgentDetailLive';
import AgentDetailQualitaet from './AgentDetailQualitaet';
import AgentTrendChart      from './AgentTrendChart';
import ZeitraumPicker       from './ZeitraumPicker';
import LoadingBar           from './LoadingBar';
import './AgentDetailView.css';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

export default function AgentSelfView({ agentId, agentName }) {
  // Zeitraum: string ('heute', 'woche') ODER Object ({from, to}) — wie AgentDetailView
  const [zeitraum,  setZeitraum]  = useState('heute');
  const [kpis,      setKpis]      = useState(null);
  const [tickets,   setTickets]   = useState([]);
  const [liveStand, setLiveStand] = useState(null);
  const [qualitaet, setQualitaet] = useState(null);
  const [loading,   setLoading]   = useState({
    kpis: false, tickets: false, live: false, qualitaet: false,
  });
  // Race-Guards: schnelles Zeitraum-Wechseln kann mehrere Requests parallel
  // auslösen — nur die Antwort der ZULETZT GESTARTETEN Anfrage darf schreiben.
  const dataReqIdRef  = useRef(0);
  const liveReqIdRef  = useRef(0);
  const trendReqIdRef = useRef(0);
  // Refresh-Trigger: erzwingt Re-Fetch auch wenn zeitraum unveraendert
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedDay,       setSelectedDay]       = useState(null);
  const [dayTickets,        setDayTickets]        = useState(null);
  const [dayTicketsLoading, setDayTicketsLoading] = useState(false);

  const [trend,        setTrend]        = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  const buildZeitraumQuery = useCallback((z) => {
    if (typeof z === 'string') return `zeitraum=${z}`;
    if (z && z.from && z.to) return `zeitraum=custom&von=${z.from}&bis=${z.to}`;
    return 'zeitraum=heute';
  }, []);

  // "Aktueller Stand" (/live) bewusst NICHT hier drin: der Endpoint nimmt gar
  // keinen Zeitraum-Parameter (zeigt immer den echten Jetzt-Stand) — würde
  // sonst bei jedem KW/Woche-Wechsel unnötig neu angefragt (gleiches Muster
  // wie AgentDetailView.jsx). Eigener Effekt weiter unten.
  const loadData = useCallback(async () => {
    if (!agentId) return;
    const myReqId = ++dataReqIdRef.current;
    setLoading(l => ({ ...l, kpis: true, tickets: true, qualitaet: true }));
    const zq = buildZeitraumQuery(zeitraum);
    const base = `${API_BASE}/api/agent-detail/${agentId}`;
    // selfView=true triggert Backend-Filter (AUSSCHLUSS_PRODUKTE, closed_by_merge,
    // pure interne Forwards). Damit sind KPIs + Liste + Live + Qualitaet konsistent.
    const sv = '&selfView=true';
    const [kpisRes, ticketsRes, qualRes] = await Promise.allSettled([
      fetch(`${base}/kpis?${zq}${sv}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${base}/tickets?${zq}&limit=all${sv}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${base}/qualitaet?${zq}${sv}`, { credentials: 'include' }).then(r => r.json()),
    ]);
    if (myReqId !== dataReqIdRef.current) return; // veraltete Antwort — verwerfen
    if (kpisRes.status === 'fulfilled') setKpis(kpisRes.value);
    if (ticketsRes.status === 'fulfilled') {
      // Backend hat schon gefiltert — Frontend muss nichts mehr filtern
      setTickets(ticketsRes.value.tickets || []);
    }
    if (qualRes.status === 'fulfilled') setQualitaet(qualRes.value);
    setLoading(l => ({ ...l, kpis: false, tickets: false, qualitaet: false }));
  }, [agentId, zeitraum, buildZeitraumQuery]);

  useEffect(() => {
    setSelectedDay(null);
    setDayTickets(null);
    loadData();
  }, [loadData, refreshTrigger]);

  // "Aktueller Stand" separat: nur bei Agentenwechsel oder "Aktualisieren".
  useEffect(() => {
    if (!agentId) { setLiveStand(null); return; }
    const myReqId = ++liveReqIdRef.current;
    setLoading(l => ({ ...l, live: true }));
    fetch(`${API_BASE}/api/agent-detail/${agentId}/live?selfView=true`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (myReqId === liveReqIdRef.current) setLiveStand(data); })
      .catch(() => { if (myReqId === liveReqIdRef.current) setLiveStand(null); })
      .finally(() => { if (myReqId === liveReqIdRef.current) setLoading(l => ({ ...l, live: false })); });
  }, [agentId, refreshTrigger]);

  const handleDayClick = useCallback(async (dayStr) => {
    if (!dayStr || !agentId) { setSelectedDay(null); setDayTickets(null); return; }
    setSelectedDay(dayStr);
    setDayTickets(null);
    setDayTicketsLoading(true);
    try {
      const url = `${API_BASE}/api/agent-detail/${agentId}/tickets?zeitraum=custom&von=${dayStr}&bis=${dayStr}&limit=all&selfView=true`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setDayTickets(data.tickets || []);
    } catch {
      setDayTickets([]);
    } finally {
      setDayTicketsLoading(false);
    }
  }, [agentId]);

  // Trend laden — Gruppe wird automatisch vom Backend aus den Tickets des Agenten ermittelt
  useEffect(() => {
    if (!agentId) { setTrend(null); return; }
    const myReqId = ++trendReqIdRef.current;
    setTrendLoading(true);
    fetch(`${API_BASE}/api/agent-detail/${agentId}/trend`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (myReqId === trendReqIdRef.current) setTrend(data); })
      .catch(() => { if (myReqId === trendReqIdRef.current) setTrend(null); })
      .finally(() => { if (myReqId === trendReqIdRef.current) setTrendLoading(false); });
  }, [agentId]);

  // Refresh-Handler: zeitraum auf 'heute' zuruecksetzen + Re-Fetch erzwingen
  const handleRefresh = useCallback(() => {
    setZeitraum('heute');
    setRefreshTrigger(r => r + 1);
  }, []);

  // Gesamt-Ladezustand — treibt Top-Ladebalken + sperrt Zeitraum-Picker/
  // Aktualisieren waehrend eine Anfrage laeuft (siehe AgentDetailView.jsx).
  const anyLoading = loading.kpis || loading.tickets || loading.live || loading.qualitaet || trendLoading;

  return (
    <div className="agent-detail-view">

      <LoadingBar active={anyLoading} />

      {/* Header — gleicher Stil wie AgentDetailView */}
      <div className="agent-detail-selectors">
        <div className="selector-group">
          <label>Mitarbeiter</label>
          <div style={{
            padding: '8px 12px',
            border: '1px solid var(--border, #d1d5db)',
            borderRadius: 6,
            fontSize: 14,
            background: 'var(--bg-secondary, #f9fafb)',
            minWidth: 220,
            color: 'var(--text-primary, #111827)',
            fontWeight: 500,
          }}>
            {agentName}
          </div>
        </div>

        <div className={`selector-group${anyLoading ? ' controls-busy' : ''}`}>
          <label>Zeitraum</label>
          <ZeitraumPicker value={zeitraum} onChange={setZeitraum} />
        </div>

        <div className="selector-group selector-group--right">
          <label>&nbsp;</label>
          <button
            type="button"
            className="refresh-btn"
            onClick={handleRefresh}
            title="Aktualisieren — laedt neu und springt auf Heute"
            disabled={anyLoading}
          >
            {anyLoading ? '⏳ Lädt…' : '↻ Aktualisieren'}
          </button>
        </div>
      </div>

      {/* Block 1 — KPIs */}
      <section className="dashboard-block">
        <h3>Kennzahlen</h3>
        <AgentDetailKPIs
          data={kpis}
          loading={loading.kpis}
          selectedDay={selectedDay}
          onDayClick={handleDayClick}
        />
      </section>

      {/* Block 2 — Ticket-Liste */}
      <section className="dashboard-block">
        <h3>
          {selectedDay
            ? (() => {
                const tag = kpis?.tage?.find(t => t.dayStr === selectedDay);
                return `Tickets — ${tag ? `${tag.wochentag} ${tag.datum}` : selectedDay}`;
              })()
            : 'Tickets'
          }
        </h3>
        <AgentDetailTickets
          tickets={selectedDay ? (dayTickets || []) : tickets}
          loading={selectedDay ? dayTicketsLoading : loading.tickets}
          hideProdukt
          total={selectedDay ? (dayTickets?.length ?? 0) : tickets.length}
        />
      </section>

      {/* Block 3 — Aktueller Stand */}
      <section className="dashboard-block">
        <h3>Aktueller Stand</h3>
        <AgentDetailLive data={liveStand} loading={loading.live} />
      </section>

      {/* Block 4 — Qualität */}
      <section className="dashboard-block">
        <h3>Qualität</h3>
        <AgentDetailQualitaet data={qualitaet} loading={loading.qualitaet} agentId={agentId} />
      </section>

      {/* Block 5 — Jahresverlauf */}
      <section className="dashboard-block">
        <h3>Jahresverlauf</h3>
        <AgentTrendChart data={trend} loading={trendLoading} />
      </section>

    </div>
  );
}
